// Listing quality gate — enforces the minimum bar for a listing to go live.
//
// Thresholds and gibberish guards are tuned CONSERVATIVE. We'd rather let a
// borderline-real listing through than block a legitimate seller writing in
// Greek or English. All checks are Unicode-aware (work over Greek script,
// not just ASCII).

export const PHOTOS_MIN     = 3;   // min photos to publish
export const TITLE_MIN      = 10;  // min title characters
export const DESCRIPTION_MIN = 150; // min description characters

// Distinct character floor. Counted over the lowercased, whitespace-stripped
// string. Picked low enough that even minimalist real text clears it — a
// Greek title like "Διαμέρισμα" already has 9 unique chars; "Sea view" has 7.
const TITLE_MIN_UNIQUE       = 3;
const DESCRIPTION_MIN_UNIQUE = 5;

function uniqueCharCount(s) {
  // toLowerCase is Unicode-aware in modern JS (handles Α→α correctly).
  const chars = s.toLowerCase().replace(/\s+/g, '');
  return new Set(chars).size;
}

function isSingleCharRepeat(s) {
  const stripped = s.replace(/\s+/g, '');
  if (stripped.length === 0) return false;
  // /u flag: match by Unicode code point, not UTF-16 code unit.
  return /^(.)\1*$/u.test(stripped);
}

// Returns { ok: true } when the title passes, or { ok: false, code, ... } with
// a stable code string that callers map to a localized message.
export function checkTitleQuality(raw, { min = TITLE_MIN } = {}) {
  const text = (raw || '').trim();
  if (text.length < min) {
    return { ok: false, code: 'titleTooShort', need: min, have: text.length };
  }
  if (isSingleCharRepeat(text)) {
    return { ok: false, code: 'titleGibberish' };
  }
  if (uniqueCharCount(text) < TITLE_MIN_UNIQUE) {
    return { ok: false, code: 'titleGibberish' };
  }
  return { ok: true };
}

export function checkDescriptionQuality(raw, { min = DESCRIPTION_MIN } = {}) {
  const text = (raw || '').trim();
  if (text.length < min) {
    return { ok: false, code: 'descriptionTooShort', need: min, have: text.length };
  }
  if (isSingleCharRepeat(text)) {
    return { ok: false, code: 'descriptionGibberish' };
  }
  // A real 150-char description in English OR Greek will contain at least
  // one whitespace. This catches `kjasdfkjasdfkjasdfkjasdf...`-style spam
  // that's written as a single 150-char token. (Title is exempt — single-
  // word listings are common.)
  if (!/\s/.test(text)) {
    return { ok: false, code: 'descriptionGibberish' };
  }
  if (uniqueCharCount(text) < DESCRIPTION_MIN_UNIQUE) {
    return { ok: false, code: 'descriptionGibberish' };
  }
  return { ok: true };
}

export function checkPhotoCount(count, { min = PHOTOS_MIN } = {}) {
  if (count >= min) return { ok: true };
  return { ok: false, code: 'photosTooFew', need: min, have: count, missing: min - count };
}
